/* ------------------------------------------------------------------
   اعلانِ پیام‌های گفتگو — از قابِ Rocket.Chat به پنل
   ------------------------------------------------------------------
   🔴 چرا پنل اعلان را نشان می‌دهد و نه خودِ Rocket.Chat.

   Rocket.Chat در قابی از دامنهٔ دیگر اجرا می‌شود و مرورگرها به
   iframeِ cross-origin اجازهٔ اعلان نمی‌دهند — اعلانِ دسکتاپِ خودش
   داخلِ پنل هیچ‌وقت ظاهر نمی‌شد. ولی هر بار که «باید» اعلان نشان
   بدهد، رویدادِ `notification` را از راهِ Iframe Integration به پنجرهٔ
   بالا می‌فرستد (همان راهی که `unread-changed` می‌آید). پنل صفحهٔ
   اصلی است و همان اجازه‌ای را دارد که `PushEnrollmentGate` گرفته.

   ⚠️ تصمیمِ «اعلان بشود یا نه» با Rocket.Chat است: سرور فقط وقتی
   این رویداد را می‌فرستد که ترجیحِ اعلانِ کاربر (پیش‌فرض: پیامِ
   مستقیم و منشن) اجازه بدهد. اینجا فقط «کجا نشان بدهیم» تصمیم
   گرفته می‌شود.
   ------------------------------------------------------------------ */

import { CHAT_APP_PATH, isChatApp } from './chatApp'

export interface ChatNotification {
  rid: string
  title: string
  body: string
  /** مسیرِ اتاق در Rocket.Chat — برای فرمانِ `go` به قاب. */
  path: string | null
  /** همان گفتگویی که الان در قاب باز است (`fromOpenedRoom` ِ Rocket.Chat). */
  fromOpenedRoom: boolean
}

const BODY_MAX = 160

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * مسیرِ اتاق در Rocket.Chat.
 *
 * ⚠️ پیامِ مستقیم با شناسهٔ اتاق باز می‌شود، کانال و گروه فقط با نام.
 * اتاقِ بی‌نام یا نوعِ ناشناخته (livechat) مسیر ندارد — کلیک فقط
 * گفتگو را باز می‌کند.
 */
function roomPath(type: string, rid: string, name: string): string | null {
  if (type === 'd') return `/direct/${encodeURIComponent(rid)}`
  if (type === 'c' && name) return `/channel/${encodeURIComponent(name)}`
  if (type === 'p' && name) return `/group/${encodeURIComponent(name)}`
  return null
}

/**
 * رویدادِ `notification` ِ Rocket.Chat به شکلی که پنل لازم دارد.
 *
 * شکلِ رویداد: `{ notification: { title, text, payload: { rid, type, name, sender } }, fromOpenedRoom, hasFocus }`.
 * ⚠️ شکلِ داده از بیرون می‌آید؛ هر چیزِ ناقص `null` می‌شود و اعلانی
 * ساخته نمی‌شود.
 */
export function parseChatNotification(raw: unknown): ChatNotification | null {
  if (!raw || typeof raw !== 'object') return null

  const outer = raw as { notification?: unknown; fromOpenedRoom?: unknown }
  const notification = (outer.notification ?? raw) as {
    title?: unknown
    text?: unknown
    payload?: { rid?: unknown; type?: unknown; name?: unknown; sender?: { name?: unknown; username?: unknown } }
  }

  const payload = notification.payload ?? {}
  const rid = text(payload.rid)
  if (!rid) return null

  /*
    🔴 ربات‌های خودِ Rocket.Chat (rocket.cat) در گفتگوی کاربن جایی ندارند
    (صاحبِ پروژه، ۱۴۰۵/۰۷/۲۹) — اعلانِ «نسخهٔ تازه» و مانندِ آن به
    ادمین نشان داده نمی‌شود؛ گفتگویش هم در خودِ چت پنهان است.
  */
  if (text(payload.sender?.username) === 'rocket.cat') return null

  const title =
    text(notification.title) || text(payload.sender?.name) || text(payload.sender?.username) || 'پیامِ تازه در گفتگو'

  let body = text(notification.text)
  if (body.length > BODY_MAX) body = `${body.slice(0, BODY_MAX - 1)}…`

  return { rid, title, body, path: roomPath(text(payload.type), rid, text(payload.name)), fromOpenedRoom: outer.fromOpenedRoom === true }
}

/**
 * اعلانِ دستگاه — فقط اگر اجازه از قبل داده شده باشد. `true` یعنی اعلان نشان داده شد.
 *
 * ⚠️ اینجا هرگز اجازه پرسیده نمی‌شود: پرسیدن بدونِ کلیکِ کاربر را
 * مرورگرها رد یا پنهان می‌کنند، و پرسیدن کارِ `PushEnrollmentGate` است.
 *
 * 🔴 ۱۴۰۵/۰۸/۰۵ «نوتیفِ چت هم تو دسکتاپ بیاد هم تو گوشی.» `new Notification` در
 * کرومِ اندروید و سافاریِ آیفون خطا می‌دهد (فقط از راهِ Service Worker مجاز است) و
 * در گوشی هیچ اعلانی نمی‌آمد. حالا همان‌جا اعلان را Service Worker ِ پنل (`sw.js`)
 * می‌سازد؛ کلیکش پیامِ `chat-open` را به همین صفحه برمی‌گرداند (ChatFrameHost) —
 * بی بارگذاریِ دوبارهٔ صفحه.
 */
export async function showChatNotification(item: ChatNotification, onClick: () => void): Promise<boolean> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return false

  const options = {
    body: item.body || undefined,
    icon: '/icons/pwa/icon-192.png',
    dir: 'rtl' as const,
    lang: 'fa-IR',
    /* پیام‌های پشتِ سرِ همِ یک اتاق روی هم می‌نشینند، نه ده اعلانِ جدا. */
    tag: `chat-${item.rid}`,
    /*
      🔴 ۱۴۰۵/۰۸/۰۹ «رو موبایل نوتیف میاد ولی پاپ‌آپ نیست». پیامِ دوم به بعدِ یک گفتگو جای قبلی می‌نشیند و
      بی `renotify` بی‌صدا عوض می‌شد: نه صدا، نه لرزش، نه پاپ‌آپ. این دو هنوز در نوع‌های TypeScript نیستند.
    */
    renotify: true,
    vibrate: [200, 100, 200],
  } as NotificationOptions

  try {
    const notification = new Notification(item.title, options)

    notification.onclick = () => {
      window.focus()
      onClick()
      notification.close()
    }

    return true
  } catch {
    /* اندروید و آیفون — پایین، از راهِ Service Worker */
  }

  try {
    const registration = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined
    if (!registration) return false

    await registration.showNotification(item.title, {
      ...options,
      badge: '/icons/pwa/icon-192.png',
      data: { url: isChatApp() ? CHAT_APP_PATH : '/app/chat', chatPath: item.path },
    })

    return true
  } catch {
    return false
  }
}
